import { createContext, useContext, useState, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { jsx } from 'react/jsx-runtime';

function _arrayWithHoles(arr) {
  if (Array.isArray(arr)) return arr;
}

function _iterableToArrayLimit(arr, i) {
  var _i = arr == null ? null : typeof Symbol !== "undefined" && arr[Symbol.iterator] || arr["@@iterator"];

  if (_i == null) return;
  var _arr = [];
  var _n = true;
  var _d = false;

  var _s, _e;

  try {
    for (_i = _i.call(arr); !(_n = (_s = _i.next()).done); _n = true) {
      _arr.push(_s.value);

      if (i && _arr.length === i) break;
    }
  } catch (err) {
    _d = true;
    _e = err;
  } finally {
    try {
      if (!_n && _i["return"] != null) _i["return"]();
    } finally {
      if (_d) throw _e;
    }
  }

  return _arr;
}

function _arrayLikeToArray(arr, len) {
  if (len == null || len > arr.length) len = arr.length;

  for (var i = 0, arr2 = new Array(len); i < len; i++) arr2[i] = arr[i];

  return arr2;
}

function _unsupportedIterableToArray(o, minLen) {
  if (!o) return;
  if (typeof o === "string") return _arrayLikeToArray(o, minLen);
  var n = Object.prototype.toString.call(o).slice(8, -1);
  if (n === "Object" && o.constructor) n = o.constructor.name;
  if (n === "Map" || n === "Set") return Array.from(o);
  if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen);
}

function _nonIterableRest() {
  throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}

function _slicedToArray(arr, i) {
  return _arrayWithHoles(arr) || _iterableToArrayLimit(arr, i) || _unsupportedIterableToArray(arr, i) || _nonIterableRest();
}

var types = ['radio', 'checkbox', 'normal'];
var variants = ['normal', 'subtle'];
var themes = ['light', 'dark'];
var sizes = ['xs', 's', 'm'];

function _arrayWithoutHoles(arr) {
  if (Array.isArray(arr)) return _arrayLikeToArray(arr);
}

function _iterableToArray(iter) {
  if (typeof Symbol !== "undefined" && iter[Symbol.iterator] != null || iter["@@iterator"] != null) return Array.from(iter);
}

function _nonIterableSpread() {
  throw new TypeError("Invalid attempt to spread non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}

function _toConsumableArray(arr) {
  return _arrayWithoutHoles(arr) || _iterableToArray(arr) || _unsupportedIterableToArray(arr) || _nonIterableSpread();
}

function arrToggle(arr, item) {
  if (arr.includes(item)) {
    return arr.filter(function (i) {
      return i !== item;
    });
  }

  return [].concat(_toConsumableArray(arr), [item]);
}

var ButtonGroupContext = /*#__PURE__*/createContext();
function useButtonGroupContext() {
  var context = useContext(ButtonGroupContext);

  if (!context) {
    throw new Error("Button group compound components cannot be rendered outside the <ButtonGroup /> component");
  }

  return context;
}

function _defineProperty(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }

  return obj;
}

function ownKeys(object, enumerableOnly) {
  var keys = Object.keys(object);

  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);
    enumerableOnly && (symbols = symbols.filter(function (sym) {
      return Object.getOwnPropertyDescriptor(object, sym).enumerable;
    })), keys.push.apply(keys, symbols);
  }

  return keys;
}

function _objectSpread2(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = null != arguments[i] ? arguments[i] : {};
    i % 2 ? ownKeys(Object(source), !0).forEach(function (key) {
      _defineProperty(target, key, source[key]);
    }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) {
      Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
    });
  }

  return target;
}

function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;

  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }

  return target;
}

function _objectWithoutProperties(source, excluded) {
  if (source == null) return {};
  var target = _objectWithoutPropertiesLoose(source, excluded);
  var key, i;

  if (Object.getOwnPropertySymbols) {
    var sourceSymbolKeys = Object.getOwnPropertySymbols(source);

    for (i = 0; i < sourceSymbolKeys.length; i++) {
      key = sourceSymbolKeys[i];
      if (excluded.indexOf(key) >= 0) continue;
      if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
      target[key] = source[key];
    }
  }

  return target;
}

var _excluded = ["className", "children", "value", "onClick", "disabled"];
var classPrefix$1 = 'forno-button-group-item'; // TODO: Currently we use a button html tag for all types of button group, would be ideal to use a
// `<input type="radio" />`
// `<input type="checkbox" />`
// Depending on the button group type

/**
 * Renders a `<ButtonGroup.Button />` component
 * @param {Object} props
 * @param {string} [props.className=] - Custom class name
 * @param {string} props.value - Value for the individual item that would define if the button is marked as active
 * @param {string} [disabled=false] - If the button should be disabled
 * @callback onClick
 */

function Button(_ref) {
  var className = _ref.className,
      children = _ref.children,
      value = _ref.value,
      _onClick = _ref.onClick,
      disabled = _ref.disabled,
      rest = _objectWithoutProperties(_ref, _excluded);

  var _useButtonGroupContex = useButtonGroupContext(),
      handleClick = _useButtonGroupContex.handleClick,
      type = _useButtonGroupContex.type,
      theme = _useButtonGroupContex.theme,
      values = _useButtonGroupContex.values,
      size = _useButtonGroupContex.size;

  var typography = "font-block-".concat(size, "-sb");
  var customClassName = classnames(classPrefix$1, className, {}, typography);

  var props = _objectSpread2({
    className: customClassName,
    onClick: function onClick(e) {
      _onClick(e, value);

      handleClick(value);
    }
  }, rest);

  var isActive = values.includes(value);
  return /*#__PURE__*/jsx("button", _objectSpread2(_objectSpread2({
    type: "button",
    "data-type": type,
    "data-state": isActive ? 'on' : 'off',
    "data-size": size,
    "data-theme": theme,
    disabled: disabled
  }, props), {}, {
    children: children
  }));
}

Button.propTypes = {
  className: PropTypes.string,
  children: PropTypes.node,
  onClick: PropTypes.func,
  disabled: PropTypes.bool,
  value: PropTypes.string.isRequired
};
Button.defaultProps = {
  children: null,
  className: null,
  onClick: function onClick() {},
  disabled: false
};

var classPrefix = 'forno-button-group';
/**
 * Renders a `<ButtonGroup />` component
 * @param {Object} props
 * @param {string} [props.className=] - Custom class name
 * @param {string} [props.type=normal] - [radio, checkbox, normal]
 * @param {string} [props.size=m] - [s, m]
 * @param {string} [props.theme=light] - [light, dark]
 * @callback onChange
 * @param {string} [initialValue=] - The initial value for the button group
 * @param {string} [variant=normal] - [normal, subtle] - subtle is for instances where
 * there are multiple toggle groups per screen, to reduce visual clutter.
 */

function ButtonGroup(_ref) {
  var className = _ref.className,
      size = _ref.size,
      theme = _ref.theme,
      type = _ref.type,
      children = _ref.children,
      onChange = _ref.onChange,
      initialValue = _ref.initialValue,
      variant = _ref.variant;
  var customClassName = classnames(classPrefix, className, {});

  var _useState = useState(initialValue),
      _useState2 = _slicedToArray(_useState, 2),
      values = _useState2[0],
      setValues = _useState2[1];

  var handleClick = useCallback(function (item) {
    switch (type) {
      case 'radio':
        onChange([item]);
        return setValues([item]);

      case 'checkbox':
        return setValues(function (arr) {
          var nArr = arrToggle(arr, item);
          onChange(nArr);
          return nArr;
        });

      default:
        onChange([item]);
        return function () {};
    }
  }, [type, onChange]);
  var state = useMemo(function () {
    return {
      type: type,
      values: values,
      theme: theme,
      size: size,
      handleClick: handleClick
    };
  }, [type, values, theme, size, handleClick]);
  return /*#__PURE__*/jsx(ButtonGroupContext.Provider, {
    value: state,
    children: /*#__PURE__*/jsx("div", {
      className: customClassName,
      "data-theme": theme,
      "data-variant": variant,
      children: children
    })
  });
}

ButtonGroup.propTypes = {
  className: PropTypes.string,
  children: PropTypes.node,
  type: PropTypes.oneOf(types),
  size: PropTypes.oneOf(sizes),
  theme: PropTypes.oneOf(themes),
  onChange: PropTypes.func,
  initialValue: PropTypes.arrayOf(PropTypes.string),
  variant: PropTypes.oneOf(variants)
};
ButtonGroup.defaultProps = {
  className: null,
  children: null,
  type: 'normal',
  size: 'm',
  theme: 'light',
  onChange: function onChange() {},
  initialValue: [],
  variant: 'normal'
};
ButtonGroup.Button = Button;

export { ButtonGroup as default };
